import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { useState } from "react";
import { store, useStore } from "@/lib/pos-store";
import { PageHeader, fmt } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, PackagePlus, Printer } from "lucide-react";
import { toast } from "sonner";
import { hasRole } from "@/lib/auth-store";
import { printHtml, fmtNum as fmtNumPrint } from "@/lib/print-utils";

export const Route = createFileRoute("/inventory")({
  beforeLoad: ({ context }) => {
    if (!hasRole(context.user, "manager")) {
      throw redirect({ to: "/" });
    }
  },
  head: () => ({
    meta: [
      { title: "Inventory" },
      { name: "description", content: "Manage SKUs and restock items." },
    ],
  }),
  component: InventoryPage,
});

function InventoryPage() {
  const items = useStore((s) => s.items);

  const handlePrint = () => {
    const tableRows = items
      .map(
        (it) => `
      <tr>
        <td>${it.sku}</td>
        <td>${it.name}</td>
        <td class="num">${it.stock}</td>
        <td class="num">${fmtNumPrint(it.buyingPrice)}</td>
        <td class="num">${fmtNumPrint(it.sellingPrice)}</td>
      </tr>`,
      )
      .join("");

    const totalStock = items.reduce((a, i) => a + i.stock, 0);
    const totalValue = items.reduce((a, i) => a + i.stock * i.sellingPrice, 0);

    const body = `
      <h1>Inventory Stock</h1>
      <div class="subtitle">${new Date().toLocaleDateString("en-KE", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</div>
      <table>
        <thead><tr><th>SKU</th><th>Item</th><th class="num">Stock</th><th class="num">Buy</th><th class="num">Sell</th></tr></thead>
        <tbody>
          ${tableRows}
          <tr class="total-row">
            <td></td><td>TOTALS</td>
            <td class="num">${totalStock}</td>
            <td class="num">—</td>
            <td class="num">${fmtNumPrint(totalValue)}</td>
          </tr>
        </tbody>
      </table>
      <div class="section-title">Summary</div>
      <div class="summary-line"><span>Total SKUs</span><span>${items.length}</span></div>
      <div class="summary-line"><span>Total Stock Value (at selling price)</span><span>${fmtNumPrint(totalValue)}</span></div>`;

    printHtml("Inventory", body);
  };

  return (
    <div>
      <PageHeader
        title="Inventory"
        subtitle={`${items.length} SKUs`}
        right={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handlePrint}>
              <Printer className="h-3.5 w-3.5" />
            </Button>
            <NewItemDialog />
          </div>
        }
      />
      <ul className="divide-y divide-border px-4">
        {items.map((it) => (
          <li key={it.id} className="flex items-center gap-3 py-3 min-h-[56px]">
            <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-accent text-accent-foreground font-display text-sm font-bold">
              {it.name.slice(0, 2).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="truncate text-sm font-medium">{it.name}</p>
              <p className="text-xs text-muted-foreground">
                {it.sku} · buy {fmt(it.buyingPrice)} · sell{" "}
                {fmt(it.sellingPrice)}
              </p>
            </div>
            <div className="text-right">
              <p
                className={`font-display text-lg font-semibold ${it.stock <= 5 ? "text-warning-foreground" : ""}`}
              >
                {it.stock}
              </p>
              <RestockDialog
                id={it.id}
                name={it.name}
                buying={it.buyingPrice}
              />
            </div>
          </li>
        ))}
      </ul>
      <p className="px-4 pt-4 text-center text-xs text-muted-foreground">
        Stock auto-adjusts with every sale.{" "}
        <Link to="/reports" className="text-primary">
          See report →
        </Link>
      </p>
    </div>
  );
}

function NewItemDialog() {
  const [open, setOpen] = useState(false);
  const [sku, setSku] = useState("");
  const [name, setName] = useState("");
  const [buy, setBuy] = useState("");
  const [sell, setSell] = useState("");

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="secondary">
          <Plus className="h-4 w-4" /> New
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New item</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>SKU</Label>
            <Input
              value={sku}
              onChange={(e) => setSku(e.target.value)}
              placeholder="ABC-001"
            />
          </div>
          <div>
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Buying</Label>
              <Input
                type="number"
                value={buy}
                onChange={(e) => setBuy(e.target.value)}
              />
            </div>
            <div>
              <Label>Selling</Label>
              <Input
                type="number"
                value={sell}
                onChange={(e) => setSell(e.target.value)}
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button
            onClick={async () => {
              if (!sku || !name || !buy || !sell)
                return toast.error("Fill all fields");
              await store.addItem({
                sku,
                name,
                buyingPrice: +buy,
                sellingPrice: +sell,
              });
              toast.success("Item added");
              setOpen(false);
              setSku("");
              setName("");
              setBuy("");
              setSell("");
            }}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RestockDialog({
  id,
  name,
  buying,
}: {
  id: string;
  name: string;
  buying: number;
}) {
  const [open, setOpen] = useState(false);
  const [qty, setQty] = useState("");
  const [cost, setCost] = useState("");
  const [payment, setPayment] = useState<"cash" | "mpesa" | "credit">("cash");
  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (v) {
          setQty("");
          setCost("");
          setPayment("cash");
        }
      }}
    >
      <DialogTrigger asChild>
        <button className="inline-flex items-center gap-1.5 text-sm font-medium text-primary py-1 px-2 rounded-lg min-h-[36px] active:bg-accent transition-colors">
          <PackagePlus className="h-3.5 w-3.5" /> Restock
        </button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Restock {name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Quantity</Label>
            <Input
              type="number"
              value={qty}
              onChange={(e) => {
                setQty(e.target.value);
                setCost(String(+e.target.value * buying || ""));
              }}
            />
          </div>
          <div>
            <Label>Total cost</Label>
            <Input
              type="number"
              value={cost}
              onChange={(e) => setCost(e.target.value)}
            />
          </div>
          <div>
            <Label>Payment method</Label>
            <div className="mt-1 flex gap-2">
              {(["cash", "mpesa", "credit"] as const).map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => setPayment(opt)}
                  className={`flex-1 rounded-md border px-3 py-2 text-sm font-medium capitalize transition-colors ${
                    payment === opt
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-input text-muted-foreground hover:bg-accent"
                  }`}
                >
                  {opt === "credit" ? "Credit/Debt" : opt}
                </button>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button
            onClick={async () => {
              if (!qty || !cost) return toast.error("Fill all fields");
              await store.addStock(id, +qty, +cost, payment);
              toast.success("Stock added");
              setOpen(false);
            }}
          >
            Add Stock
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
